import { useEffect, useRef, useState } from 'react';
import {
  IMAGE_CATEGORIES,
  IMAGE_CATEGORY_LABELS,
  type ImageCategory,
  type Strain,
} from '@shared';
import { getProductImagesApi } from '../lib/productImages';
import { rotateImage } from '../lib/imageProcessing';

/**
 * Select or capture an image, preview it, rotate if needed, tag its category,
 * and persist it for a strain (server upload or local IndexedDB via the
 * centralized product images API).
 */
export interface ImageUploaderProps {
  strainId: string;
  onUploaded: (strain: Strain) => void;
}

export function ImageUploader({
  strainId,
  onUploaded,
}: ImageUploaderProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [category, setCategory] = useState<ImageCategory>('package_front');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const reset = (): void => {
    setFile(null);
    setRotation(0);
    setError(null);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (libraryInputRef.current) libraryInputRef.current.value = '';
  };

  const onPick = (f: File | null): void => {
    setFile(f);
    setRotation(0);
    setError(null);
  };

  const handleUpload = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await rotateImage(file, rotation);
      const strain = await getProductImagesApi().upload(
        strainId,
        blob,
        category,
      );
      onUploaded(strain);
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: '0.75rem' }}>
      <div className="field" style={{ margin: 0 }}>
        <span id="image-input-label">Choose or capture an image</span>
        <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn"
            onClick={() => cameraInputRef.current?.click()}
            disabled={busy}
          >
            Take photo
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => libraryInputRef.current?.click()}
            disabled={busy}
          >
            Choose from library
          </button>
        </div>
        <input
          id="image-input-camera"
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="visually-hidden"
          aria-labelledby="image-input-label"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
        <input
          id="image-input-library"
          ref={libraryInputRef}
          type="file"
          accept="image/*"
          className="visually-hidden"
          aria-labelledby="image-input-label"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
        <span className="hint">
          JPEG, PNG, or WebP. Processed on this device. Local mode stores photos
          in this browser only.
        </span>
      </div>

      {previewUrl && (
        <div className="stack" style={{ gap: '0.5rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              background: 'var(--color-surface-2)',
              borderRadius: '8px',
              padding: '0.5rem',
            }}
          >
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '260px',
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.15s ease',
              }}
            />
          </div>
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() =>
                setRotation((r) => (((r + 270) % 360) as 0 | 90 | 180 | 270))
              }
            >
              ⟲ Rotate left
            </button>
            <button
              type="button"
              className="btn"
              onClick={() =>
                setRotation((r) => (((r + 90) % 360) as 0 | 90 | 180 | 270))
              }
            >
              ⟳ Rotate right
            </button>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="image-category">Image type</label>
            <select
              id="image-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ImageCategory)}
            >
              {IMAGE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {IMAGE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>

          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Save photo'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={reset}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="notice" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
